import type {
  PreparedAttendanceSession,
  PrepareSessionLinkIntent,
} from '@src/models/interfaces/prepareAttendanceSession.interface';
import { PREPARE_SESSION_LINK_INTENT } from '@src/models/interfaces/prepareAttendanceSession.interface';
import PrepareAttendanceSessionRepository from '@src/repositories/PrepareAttendanceSessionRepository';
import safeExecute from '@src/util/safeExecute';

export interface PrepareAttendanceRecordInput {
  organizationId: string;
  shiftStartAtDevice: string;
  /** Admin-only target employee; omit when preparing the authenticated employee's record. */
  employeeId?: string;
}

class PrepareAttendanceSessionService {
  private readonly repository: PrepareAttendanceSessionRepository;

  constructor(repository: PrepareAttendanceSessionRepository = new PrepareAttendanceSessionRepository()) {
    this.repository = repository;
  }

  /**
   * Mints an unassigned ad-hoc attendance record when the current day has no record yet.
   * Used before the first marking by the Marking Portal.
   */
  prepareAdhocRecord(input: PrepareAttendanceRecordInput): Promise<PreparedAttendanceSession> {
    return this.prepareRecord(input, PREPARE_SESSION_LINK_INTENT.UNASSIGNED);
  }

  /**
   * Resolves or creates the official journey attendance record for the day.
   * Used before the first supervisor marking on Work Journey Real-Time.
   */
  prepareOfficialRecord(input: PrepareAttendanceRecordInput): Promise<PreparedAttendanceSession> {
    return this.prepareRecord(input, PREPARE_SESSION_LINK_INTENT.OFFICIAL);
  }

  private prepareRecord(
    input: PrepareAttendanceRecordInput,
    linkIntent: PrepareSessionLinkIntent,
  ): Promise<PreparedAttendanceSession> {
    const methodName =
      linkIntent === PREPARE_SESSION_LINK_INTENT.OFFICIAL
        ? 'PrepareAttendanceSessionService.prepareOfficialRecord'
        : 'PrepareAttendanceSessionService.prepareAdhocRecord';

    return safeExecute(
      async () => {
        const organizationId = input?.organizationId?.trim();
        const shiftStartAtDevice = input?.shiftStartAtDevice?.trim();
        const employeeId = input?.employeeId?.trim();

        if (!organizationId || !shiftStartAtDevice) {
          throw new Error('Missing required parameters: organizationId, shiftStartAtDevice');
        }
        if (input?.employeeId !== undefined && !employeeId) {
          throw new Error('employeeId must be a non-empty string when provided');
        }

        const localSessionId =
          typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
            ? crypto.randomUUID()
            : `attendance-session-${Date.now()}`;

        const response = await this.repository.prepareAttendanceSessions({
          organizationId,
          ...(employeeId ? { employeeId } : {}),
          sessions: [
            {
              localSessionId,
              shiftStartAtDevice,
              linkIntent,
            },
          ],
        });

        const prepared = response.sessions?.[0];
        if (!prepared?.recordId) {
          throw new Error(
            linkIntent === PREPARE_SESSION_LINK_INTENT.OFFICIAL
              ? 'Failed to prepare official attendance record'
              : 'Failed to prepare ad-hoc attendance record',
          );
        }

        return prepared;
      },
      methodName,
      { organizationId: input?.organizationId },
    );
  }
}

export default PrepareAttendanceSessionService;
